/**
 * ステージのマスター詳細の欄。名前の作り（目的語（種別）＋ 動詞（名詞形））・入口（業務 → 結末）・
 * 出口・説明を 1 行ずつに畳む。
 *
 * 欄を別々の行に並べると、1 つの宣言の 2 つの部品（目的語と動詞、業務と結末）に見えない。種別と
 * 名詞形はマスター詳細の読取に無い（欄は参照の番号と表示名だけを持つ）ので、用語集と動詞の一覧の
 * 読取から引く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージのマスター詳細の欄を、名前の作り・入口・出口・説明の 1 行ずつに畳んで描く。
 * @graph-connects useListTerms [calls] 用語集（目的語の種別）
 * @graph-connects useListMasters [calls] 動詞の一覧（名詞形）
 */
import type { MasterDetailOutput } from '@cortex/domain-spec-console';
import { useTranslation } from 'react-i18next';

import {
  toElementNumber,
  useListMasters,
} from '../hooks/queries/spec-graph-element-master-queries.js';
import { useListTerms } from '../hooks/queries/spec-graph-element-term-queries.js';
import {
  fieldLabelKey,
  MasterFieldValue,
  UnsetValue,
  type MasterField,
} from './master-field-value.js';
import { enumLabel } from './TermsPage.js';

/**
 * ステージの名前の作りに要る材料。目的語の用語の種別と、動詞の名詞形。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの名前の作り（目的語の種別・動詞の名詞形）を引くための材料
 * @graph-connects none
 */
export interface StageNameMaterials {
  /** 目的語の用語の種別。未設定・一覧に無ければ null */
  sectionOf: (termId: number) => string | null;
  /** 動詞の名詞形。未設定・一覧に無ければ null */
  nounFormOf: (verbId: number) => string | null;
}

/**
 * ステージの名前の作りの材料を、用語集と動詞の一覧から引く。
 *
 * @param pj 案件番号。案件を重ねた姿の材料を引くとき渡す。master なら undefined
 * @returns 目的語の種別と動詞の名詞形の引き方
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの名前の作りに出す目的語の種別と動詞の名詞形を、用語集と動詞の一覧から引く。
 * @graph-connects useListTerms [calls] 用語集（種別）
 * @graph-connects useListMasters [calls] 動詞の一覧（名詞形）
 * @graph-connects toElementNumber [calls] 案件番号を数へ読む
 */
export function useStageNameMaterials(pj: string | undefined): StageNameMaterials {
  const terms = useListTerms(toElementNumber(pj));
  const verbs = useListMasters('verb', pj);
  const sections = new Map((terms.data?.items ?? []).map((term) => [term.termId, term.section]));
  const nounForms = new Map((verbs.data?.items ?? []).map((verb) => [verb.id, verb.nounForm]));
  return {
    sectionOf: (termId) => sections.get(termId) ?? null,
    nounFormOf: (verbId) => {
      const nounForm = nounForms.get(verbId);
      return nounForm === undefined || nounForm === '' ? null : nounForm;
    },
  };
}

/**
 * ステージの欄を画面の行へ畳む決まり。名前の作りは 目的語 ＋ 動詞、入口と出口は 業務 → 結末。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの欄を、名前の作り・入口・出口の 1 行ずつに畳む対応表
 * @graph-connects none
 */
const STAGE_FIELD_GROUPS = [
  { key: 'nameParts', fields: ['subject', 'verb'] },
  { key: 'entry', fields: ['entry.business', 'entry.ending'] },
  { key: 'exit', fields: ['exit.business', 'exit.ending'] },
] as const;

/** ステージの行 1 つ。行の鍵と、畳んだ欄名。 @graph-connects none */
export interface StageFieldGroup {
  /** 行の鍵。表示名の翻訳キーもここから引く */
  key: string;
  /** 畳んだ欄名（畳まない欄は 1 つ） */
  fields: readonly string[];
}

/**
 * ステージの欄名の並びを、画面の行へ畳む。畳む対象に無い欄（説明など）は 1 欄 1 行で後ろに並べる。
 *
 * @param fieldNames 契約の欄名の並び
 * @returns 画面の行の並び（名前の作り → 入口 → 出口 → 残りの欄）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの欄名を、名前の作り・入口・出口の行と、それ以外の 1 欄 1 行に畳む。
 * @graph-connects none
 */
export function stageFieldGroups(fieldNames: readonly string[]): StageFieldGroup[] {
  const grouped = new Set<string>(STAGE_FIELD_GROUPS.flatMap((group) => group.fields));
  return [
    ...STAGE_FIELD_GROUPS.map((group) => ({ key: group.key, fields: group.fields })),
    ...fieldNames
      .filter((name) => !grouped.has(name))
      .map((name) => ({ key: name, fields: [name] })),
  ];
}

/**
 * ステージの欄の行（名前の作り・入口・出口・説明）。
 *
 * 名前の作りの材料（目的語の種別・動詞の名詞形）は `pj` の姿から引く。案件で何も変わって
 * いないステージを案件モードで 1 面に描くときも、用語や動詞は案件で変わりうるので案件の材料を
 * 使う（左右比較の変更後と同じ材料になる）。
 *
 * `include` を渡すと、その行の鍵（`nameParts` / `entry` / `exit` / 畳まない欄名）で真を返した行だけを
 * 描く。入口と出口だけを先に出し、残りを畳んだ中に出すときに使う。
 *
 * @param props ステージのマスター詳細と、材料を引く案件番号（省けば案件なし）と、描く行の選び方（省けば全部）
 * @returns 欄の行
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの欄を、名前の作り・入口・出口を 1 行ずつに畳んで欄名と値で描き、名前の作りの材料は重ねている案件の姿から引く。
 * @graph-connects useStageNameMaterials [calls] 名前の作りの材料
 * @graph-connects stageFieldGroups [calls] ステージの欄の畳み方
 * @graph-connects StageGroupValue [calls] 行 1 つの値
 * @graph-connects fieldLabelKey [calls] 行の見出しの翻訳キー
 */
export function StageFieldRows({
  detail,
  pj,
  include,
}: {
  detail: MasterDetailOutput;
  pj?: string;
  include?: (groupKey: string) => boolean;
}) {
  const { t } = useTranslation();
  const materials = useStageNameMaterials(pj);
  const groups = stageFieldGroups(detail.fields.map((field) => field.field)).filter(
    (group) => include?.(group.key) ?? true,
  );

  return (
    <>
      {groups.map((group) => (
        <div
          key={group.key}
          data-stage-row={group.key}
          className="flex flex-wrap items-baseline gap-3 border-b border-[var(--color-border)] py-2 last:border-b-0"
        >
          <dt className="min-w-32 text-xs text-[var(--color-text-muted)]">
            {t(fieldLabelKey(group.key))}
          </dt>
          <dd className="min-w-0 flex flex-wrap items-baseline gap-2 text-sm text-[var(--color-text-primary)]">
            <StageGroupValue group={group} detail={detail} materials={materials} />
          </dd>
        </div>
      ))}
    </>
  );
}

/** `StageGroupValue` の props。 @graph-connects none */
export interface StageGroupValueProps {
  group: StageFieldGroup;
  detail: MasterDetailOutput;
  materials: StageNameMaterials;
}

/**
 * ステージの行 1 つの値。名前の作りは 目的語（種別）＋ 動詞（名詞形）、入口と出口は 業務 → 結末。
 *
 * @param props 行・ステージの詳細・名前の作りの材料
 * @returns 行の値
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの行 1 つを、名前の作り・業務と結末の組・そのほかの欄の別に応じて描く。
 * @graph-connects stageNamePartsText [calls] 名前の作りの 1 行
 * @graph-connects stageEndingText [calls] 業務 → 結末 の 1 行
 * @graph-connects MasterFieldValue [calls] 畳まない欄の値
 * @graph-connects UnsetValue [calls] 値の無い欄
 */
export function StageGroupValue({ group, detail, materials }: StageGroupValueProps) {
  const { t } = useTranslation();
  const [first, second] = group.fields.map((name) =>
    detail.fields.find((field) => field.field === name),
  );
  if (group.key === 'nameParts') {
    const text = stageNamePartsText(t, first, second, materials);
    return text === null ? <UnsetValue /> : <span>{text}</span>;
  }
  if (group.key === 'entry' || group.key === 'exit') {
    const text = stageEndingText(t, first, second);
    return text === null ? <UnsetValue /> : <span>{text}</span>;
  }
  return first === undefined ? <UnsetValue /> : <MasterFieldValue field={first} />;
}

/** 翻訳関数の形。 @graph-connects none */
type Translate = (key: string, params?: Record<string, unknown>) => string;

/** 業務 → 結末 を繋ぐ記号。どの言語でも同じ。 @graph-connects none */
const ENDING_ARROW = ' → ';

/**
 * 参照の欄の番号と表示名。欄が無い・参照が無ければ null、表示名が組めなければ名前は「未設定」。
 * @graph-connects none
 */
function refOf(
  t: Translate,
  field: MasterField | undefined,
): { readonly id: number; readonly label: string } | null {
  if (field === undefined || field.refId === null) return null;
  return { id: field.refId, label: field.text === '' ? t('masterDetail.unset') : field.text };
}

/**
 * 名前の作りを 1 行にする。目的語は種別を、動詞は名詞形を添える（表示名は名詞形で読むため）。
 *
 * @param t 翻訳関数
 * @param subject 目的語の欄
 * @param verb 動詞の欄
 * @param materials 目的語の種別と動詞の名詞形の引き方
 * @returns 1 行の文言。目的語も動詞も無ければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの名前の作りを「目的語（種別）＋ 動詞（名詞形）」の 1 行にする。
 * @graph-connects enumLabel [calls] 種別の訳語
 * @graph-connects refOf [calls] 参照の欄の番号と表示名
 */
export function stageNamePartsText(
  t: Translate,
  subject: MasterField | undefined,
  verb: MasterField | undefined,
  materials: StageNameMaterials,
): string | null {
  const subjectRef = refOf(t, subject);
  const verbRef = refOf(t, verb);
  if (subjectRef === null && verbRef === null) return null;
  const subjectPart =
    subjectRef === null
      ? t('masterDetail.unset')
      : t('masterDetail.nameSubject', {
          label: subjectRef.label,
          section: enumLabel(t, 'termSection', materials.sectionOf(subjectRef.id)),
        });
  const nounForm = verbRef === null ? null : materials.nounFormOf(verbRef.id);
  const verbPart =
    verbRef === null
      ? t('masterDetail.unset')
      : nounForm === null
        ? t('masterDetail.nameVerbNoNoun', { label: verbRef.label })
        : t('masterDetail.nameVerb', { label: verbRef.label, nounForm });
  return `${subjectPart}${t('masterDetail.namePartsJoiner')}${verbPart}`;
}

/**
 * 入口・出口を 業務 → 結末 の 1 行にする。
 *
 * @param t 翻訳関数
 * @param business 業務の欄
 * @param ending 結末の欄
 * @returns 1 行の文言。業務も結末も無ければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの入口・出口を「業務 → 結末」の 1 行にする。
 * @graph-connects refOf [calls] 参照の欄の番号と表示名
 */
export function stageEndingText(
  t: Translate,
  business: MasterField | undefined,
  ending: MasterField | undefined,
): string | null {
  const businessRef = refOf(t, business);
  const endingRef = refOf(t, ending);
  if (businessRef === null && endingRef === null) return null;
  return [businessRef, endingRef]
    .map((ref) => (ref === null ? t('masterDetail.unset') : ref.label))
    .join(ENDING_ARROW);
}
